/* eslint-disable global-require */
import React, { useContext, useState } from 'react'
import { useInitialData } from '@notino/react-toolkit/renderer/fragment/useInitialData'
import { useIntl } from 'react-intl'
import axios from 'axios'
import { HttpStatus } from '@notino/react-toolkit/renderer/fragment/contexts/HttpContext'
import { IBreadcrumb } from '@notino/react-styleguide'

// components
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb'
import Reservation from './components/Reservation/Reservation'
import CancelReservationModal from './components/CancelReservationModal/CancelReservationModal'
import FetchResult from '../../components/FetchResult/FetchResult'
// import ReservationBookingModal from '../../components/ReservationBookingModal/ReservationBookingModal'
import MyReservationPageHead from './components/MyReservationPageHead/MyReservationPageHead'

// types
import {
	ContextProps,
	IGetInitialDataProps,
	ConfigResponse,
	// SalonServicesResponse,
	ReservationResponse,
	MyReservationPageQueryType,
	MyReservationPageSetQueryType,
	// ReservationBookingModalInitData,
	StripUndefined,
	CmsMyReservationPageData
} from '../../types/types'

// utils
import { AppContext } from '../../utils/appProvider'
// import { PAGE_LINKS } from '../../utils/helper'
import { ERROR_PAGE_CODE } from '../../utils/enums'

// hooks
import useMessages from '../../hooks/useMessages'

// styles
import * as SC from './MyReservationPageStyles'

type ExtendedContextProps = ContextProps & {
	reservationID: string
	query: MyReservationPageQueryType
	setQuery: MyReservationPageSetQueryType
}

type RequiredInitialData = {
	// salonServicesData: SalonServicesResponse | undefined
	reservationData: ReservationResponse | undefined
	configData: ConfigResponse | undefined
}

type OptionalInitialData = {
	myReservationPageCmsData: CmsMyReservationPageData | undefined
}

type InitialData = RequiredInitialData &
	OptionalInitialData & {
		errorPageCode?: ERROR_PAGE_CODE
	}

type MyReservationPageProps = StripUndefined<RequiredInitialData> &
	OptionalInitialData &
	ExtendedContextProps & {
		setReservationData: React.Dispatch<React.SetStateAction<ReservationResponse | undefined>>
	}

const MyReservationPage = (props: MyReservationPageProps) => {
	const { reservationID /* , salonServicesData */, configData, reservationData, myReservationPageCmsData, setReservationData } = props
	const { locale } = useIntl()
	const { messages } = useMessages()
	const { apiBrowser, assetsPath } = useContext(AppContext)
	const [isLoadingReservation, setIsLoadingReservation] = useState(false)
	const token = props.query.t

	// NOTE - temporary disabled to book again
	// const [reservationBookingModalInitData, setReservationBookingModalInitData] = useState<ReservationBookingModalInitData | null>(null)
	const [cancelReservationModalData, setCancelReservationModalData] = useState<{ reservationID: string } | null>()

	const fetchReservationData = async () => {
		setIsLoadingReservation(true)
		const config = {
			headers: {
				Authorization: `Bearer ${token}`
			}
		}
		const response = await apiBrowser.b2c.getReservationData(reservationID, undefined, config)
		setReservationData(response)
		setIsLoadingReservation(false)
	}

	const onCancelReservation = () => {
		setCancelReservationModalData({ reservationID })
	}

	const onCancelReservationSubmitSuccess = () => {
		fetchReservationData()
		setCancelReservationModalData(null)
	}

	/* const onBookAgain = (reservation: NonNullable<ReservationResponse['reservation']>) => {
		const reservationModalInitData: ReservationBookingModalInitData = {
			initIndustryID: null,
			initServiceID: reservation.service?.id ?? null,
			initAvResTimeSlotDayPart: null,
			initServiceCategoryParameterID:
				reservation.service?.useCategoryParameter && reservation.service?.serviceCategoryParameter?.id
					? reservation.service.serviceCategoryParameter.id
					: null,
			parameterSelectionData: null,
			employeeAndDateSelectionData: {
				employeeID: reservation.employee.id
			},
			bookingConfirmationData: {
				email: reservation.customer?.email,
				isVisitor: reservation.customer?.isVisitor,
				visitor:
					reservation.customer?.isVisitor && reservation.customer.firstName && reservation.customer.lastName
						? { firstName: reservation.customer.firstName, lastName: reservation.customer.lastName }
						: undefined,
				phone:
					reservation.customer?.phone && reservation.customer?.phonePrefixCountryCode
						? {
								phoneNumber: reservation.customer?.phone,
								phonePrefixCountryCode: reservation.customer?.phonePrefixCountryCode
							}
						: undefined
			}
		}
		setReservationBookingModalInitData(reservationModalInitData)
	} */

	const promoBannerLink = myReservationPageCmsData?.reservationDetailBannerData?.image.link || ''
	const promoBannerAlt = myReservationPageCmsData?.reservationDetailBannerData?.image.alt || messages['app-promo-banner-alt-text']

	// NOTE: mobile banner doesn't exist for now
	let bannerSrc: string

	switch (locale) {
		case 'ro':
			bannerSrc = `${assetsPath}/${require(`../../assets/images/reservations/side-banner-ro.jpg`)}`
			break
		case 'bg':
			bannerSrc = `${assetsPath}/${require(`../../assets/images/reservations/side-banner-bg.jpg`)}`
			break
		case 'hu':
			bannerSrc = `${assetsPath}/${require(`../../assets/images/reservations/side-banner-hu.jpg`)}`
			break
		case 'sk':
			bannerSrc = `${assetsPath}/${require(`../../assets/images/reservations/side-banner-sk.jpg`)}`
			break
		case 'cs':
		default:
			bannerSrc = `${assetsPath}/${require(`../../assets/images/reservations/side-banner-cs.jpg`)}`
			break
	}

	if (myReservationPageCmsData?.reservationDetailBannerData?.image.desktop) {
		bannerSrc = myReservationPageCmsData.reservationDetailBannerData.image.desktop
	}

	const breadcrumbItems: IBreadcrumb[] = [
		/* { link: PAGE_LINKS['/salons/my-reservations'](locale), name: messages['My reservations'] */ { name: messages['Reservation detail'], link: '' }
	]

	const promoBannerImage = <SC.PromoBannerImage src={bannerSrc} alt={promoBannerAlt} />

	return (
		<>
			<MyReservationPageHead reservation={reservationData.reservation} />

			<SC.PageContainer>
				<Breadcrumb breadcrumbItems={breadcrumbItems} hasSalonsBreadcrumb />
				<SC.MainContent>
					<SC.ReservationContainer>
						<FetchResult isRefetching={isLoadingReservation}>
							<Reservation
								reservation={reservationData.reservation}
								// salonServicesData={salonServicesData}
								onCancelReservation={onCancelReservation}
								// onBookAgain={onBookAgain}
							/>
						</FetchResult>
					</SC.ReservationContainer>
					<SC.SidebarContainer>
						{promoBannerLink ? (
							<SC.PromoBannerLink href={promoBannerLink} target={'blank'} rel={'noopener noreferrer'}>
								{promoBannerImage}
							</SC.PromoBannerLink>
						) : (
							promoBannerImage
						)}
					</SC.SidebarContainer>
				</SC.MainContent>
			</SC.PageContainer>
			{/** // NOTE - temporary disabled to book again  */}
			{/* reservationBookingModalInitData && salonServicesData && (
				<ReservationBookingModal
					salonData={{
						phones: reservationData.reservation.salon.phones,
						id: reservationData.reservation.salon.id,
						availableReservationSystem: true,
						images: reservationData.reservation.salon.images
					}}
					isOpen
					initData={reservationBookingModalInitData}
					onClose={() => setReservationBookingModalInitData(null)}
					salonServicesData={salonServicesData}
					configData={configData}
				/>
			) */}
			{cancelReservationModalData && (
				<CancelReservationModal
					isOpen
					token={token}
					reservationID={cancelReservationModalData.reservationID}
					reservationCancellationQuickFeedbackAnswers={configData.reservationCancellationQuickFeedbackAnswers}
					onClose={() => setCancelReservationModalData(null)}
					onSubmitSuccess={onCancelReservationSubmitSuccess}
				/>
			)}
		</>
	)
}

// page container
const MyReservationPageContainer: IGetInitialDataProps<InitialData | null, ExtendedContextProps> = (props) => {
	const data = useInitialData(MyReservationPageContainer, props)

	//  save data to state to allow client-side refetching
	const [reservationData, setReservationData] = useState<ReservationResponse | undefined>(data?.reservationData)

	// necessary for notino toolkit
	if (!data) {
		return null
	}

	if (data?.errorPageCode) {
		return (
			<HttpStatus status={data.errorPageCode} {...props}>
				{null}
			</HttpStatus>
		)
		return null
	}

	if (!reservationData || /* !data.salonServicesData || */ !data.configData) {
		return (
			<HttpStatus status={ERROR_PAGE_CODE.GENERAL_ERROR} {...props}>
				{null}
			</HttpStatus>
		)
	}

	return (
		<MyReservationPage
			{...props}
			// salonServicesData={data.salonServicesData}
			configData={data.configData}
			reservationData={reservationData}
			myReservationPageCmsData={data.myReservationPageCmsData}
			setReservationData={setReservationData}
		/>
	)
}

MyReservationPageContainer.initDefaultData = async ({ api, reservationID, query, isAuthorized }) => {
	let initialData: InitialData = {
		reservationData: undefined,
		// salonServicesData: undefined,
		configData: undefined,
		myReservationPageCmsData: undefined
	}

	try {
		const config = isAuthorized
			? undefined
			: {
					headers: {
						Authorization: `Bearer ${query.t}`
					}
				}
		const [reservationData, configData, myReservationPageCmsData] = await Promise.all([
			api.b2c.getReservationData(reservationID, undefined, config),
			api.b2c.getConfigData(),
			api.cms.getMyReservationPageCmsData()
		])

		initialData = {
			...initialData,
			reservationData,
			configData,
			myReservationPageCmsData
		}

		// NOTE - temporary disabled to book again
		/* if (reservationData.reservation?.salon.id) {
			const salonServicesData = await api.b2c.getSalonServicesData(reservationData.reservation.salon.id)

			initialData = {
				...initialData,
				salonServicesData
			}
		} */
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 404 || error.response?.status === 400 || error.response?.status === 409 || error.response?.status === 403) {
				initialData = {
					...initialData,
					errorPageCode: ERROR_PAGE_CODE.NOT_FOUND
				}
			}

			if (error.response?.status === 401) {
				initialData = {
					...initialData,
					errorPageCode: ERROR_PAGE_CODE.UNAUTHORIZED
				}
			}
		}
	}

	return initialData
}

MyReservationPageContainer.identifier = 'MyReservationPage'

export default MyReservationPageContainer
